import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import PageHeader, { PageBackground } from '@domain/page-header';

import OtpForm, { type OtpFormValues } from './otp-form';

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 24px;
`;

const FormArea = styled.div`
  background: ${({ theme }) => theme.white};
  border-radius: 30px;
  width: 40vw;
  padding: 36px;
  @media only screen and (max-width: 992px) {
    width: 80vw;
    padding: 24px;
  }
  @media only screen and (max-width: 480px) {
    padding: 20px 16px;
    border-radius: 16px;
  }
`;

const FormHeader = styled.h1`
  font-size: 36px;
  font-weight: 600;
  text-align: center;
  margin-top: 20px;
  color: ${({ theme }) => theme.text};
  @media only screen and (max-width: 768px) {
    font-size: 24px;
    margin-top: 0;
  }
`;

const FormHeaderDesc = styled.h6`
  font-size: 18px;
  font-weight: 500;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 48px;
  @media only screen and (max-width: 768px) {
    font-size: 14px;
    margin: 12px 0;
  }
`;

const RESEND_COOLDOWN = 60;

interface OtpFeatureProps {
  onVerify: (otp: string) => Promise<void>;
  onResend: () => Promise<void>;
  isPending: boolean;
  isResending: boolean;
}

function OtpFeature({
  onVerify,
  onResend,
  isPending,
  isResending
}: OtpFeatureProps) {
  const intl = useIntl();
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current ?? undefined);
  }, [cooldown]);

  const handleFormSubmit = async (data: OtpFormValues) => {
    try {
      await onVerify(data.otp);
    } catch {}
  };

  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;
    try {
      await onResend();
      setCooldown(RESEND_COOLDOWN);
    } catch {}
  };

  return (
    <PageBackground>
      <PageHeader />
      <Container>
        <FormArea>
          <FormHeader>{intl.formatMessage({ id: 'OTP_HEADER' })}</FormHeader>
          <FormHeaderDesc>
            {intl.formatMessage({ id: 'OTP_DESC' })}
          </FormHeaderDesc>
          <OtpForm
            onSubmit={handleFormSubmit}
            isPending={isPending}
            isResending={isResending}
            cooldown={cooldown}
            onResendClick={handleResend}
          />
        </FormArea>
      </Container>
    </PageBackground>
  );
}

export default OtpFeature;
